import { supabase } from '../lib/supabase';

export class SearchService {
  /**
   * Search projects globally across multiple fields
   * @param {string} searchTerm - The search query
   * @param {number} limit - Maximum number of results to return
   * @returns {Promise<{data: Array, totalCount: number, error: Error|null}>}
   */
  static async searchProjects(searchTerm, limit = 10) {
    try {
      if (!searchTerm || searchTerm.trim().length === 0) {
        return { data: [], totalCount: 0, error: null };
      }

      const trimmedSearch = searchTerm.trim().toLowerCase();
      
      // Use Supabase text search with OR conditions across multiple fields
      // We'll use ilike for case-insensitive partial matching
      const { data, error, count } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          job_number,
          rev,
          modeling_status,
          modeling_state,
          billing_status,
          due_date,
          projected_completion_date,
          completed_date,
          stage_status,
          is_high_priority,
          client_id,
          contact_id,
          model_builder_id,
          ready_for_employee_id,
          clients (
            id,
            name
          )
        `, { count: 'exact' })
        .or(`name.ilike.%${trimmedSearch}%,job_number.ilike.%${trimmedSearch}%`)
        .order('job_number', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Search error:', error);
        throw error;
      }

      // Also search by client name (requires a separate query due to Supabase limitations)
      const { data: clientSearchData, error: clientError } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          job_number,
          rev,
          modeling_status,
          modeling_state,
          billing_status,
          due_date,
          projected_completion_date,
          completed_date,
          stage_status,
          is_high_priority,
          client_id,
          contact_id,
          model_builder_id,
          ready_for_employee_id,
          clients!inner (
            id,
            name
          )
        `)
        .ilike('clients.name', `%${trimmedSearch}%`)
        .order('job_number', { ascending: false })
        .limit(limit);

      if (clientError && clientError.code !== 'PGRST116') {
        console.error('Client search error:', clientError);
      }

      // Combine and deduplicate results
      const combinedData = [...(data || [])];
      const projectIds = new Set(combinedData.map(p => p.id));
      
      if (clientSearchData) {
        clientSearchData.forEach(project => {
          if (!projectIds.has(project.id)) {
            combinedData.push(project);
            projectIds.add(project.id);
          }
        });
      }

      // Sort combined results by job number (highest to lowest) and limit
      const sortedData = combinedData
        .sort((a, b) => {
          // Convert job numbers to integers for proper numeric sorting
          const aJobNum = parseInt(a.job_number) || 0;
          const bJobNum = parseInt(b.job_number) || 0;
          
          // Sort by job number (highest to lowest)
          return bJobNum - aJobNum;
        })
        .slice(0, limit);

      // Transform data to frontend format
      const transformedData = sortedData.map(project => ({
        id: project.id,
        projectName: project.name,
        jobNumber: project.job_number,
        rev: project.rev,
        modelingStatus: project.modeling_status,
        modelingState: project.modeling_state,
        billingStatus: project.billing_status,
        dueDate: project.due_date,
        projectedCompletionDate: project.projected_completion_date,
        completedDate: project.completed_date,
        stageStatus: project.stage_status,
        isHighPriority: project.is_high_priority,
        clientId: project.client_id,
        clientName: project.clients?.name || '',
        contactId: project.contact_id,
        contactName: '', // Will need to fetch separately if needed
        contactEmail: '',
        contactPhone: '',
        modelBuilderId: project.model_builder_id,
        modelBuilderName: '', // Will need to fetch separately if needed
        readyForEmployeeId: project.ready_for_employee_id,
        readyForEmployeeName: ''
      }));

      // Get total count if there are more results
      const totalCount = combinedData.length > limit ? combinedData.length : count || combinedData.length;

      return {
        data: transformedData,
        totalCount,
        error: null
      };
    } catch (error) {
      console.error('SearchService error:', error);
      return {
        data: [],
        totalCount: 0,
        error: error.message || 'Failed to search projects'
      };
    }
  }

  /**
   * Search contacts globally across multiple fields
   * @param {string} searchTerm - The search query
   * @param {number} limit - Maximum number of results to return
   * @returns {Promise<{data: Array, totalCount: number, error: Error|null}>}
   */
  static async searchContacts(searchTerm, limit = 10) {
    try {
      if (!searchTerm || searchTerm.trim().length === 0) {
        return { data: [], totalCount: 0, error: null };
      }

      const trimmedSearch = searchTerm.trim().toLowerCase();
      
      // Search contacts by name, email, and phone (excluding role since it's an enum)
      const { data, error, count } = await supabase
        .from('contacts')
        .select(`
          id,
          name,
          email,
          phone_number,
          role,
          client_id,
          clients (
            id,
            name
          )
        `, { count: 'exact' })
        .or(`name.ilike.%${trimmedSearch}%,email.ilike.%${trimmedSearch}%,phone_number.ilike.%${trimmedSearch}%`)
        .order('name', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('Contact search error:', error);
        throw error;
      }

      // Transform data to frontend format
      const transformedData = (data || []).map(contact => ({
        id: contact.id,
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone_number || '',
        role: contact.role || '',
        clientId: contact.client_id,
        clientName: contact.clients?.name || '',
        type: 'contact' // Add type identifier for search results
      }));

      return {
        data: transformedData,
        totalCount: count || transformedData.length,
        error: null
      };
    } catch (error) {
      console.error('Contact SearchService error:', error);
      return {
        data: [],
        totalCount: 0,
        error: error.message || 'Failed to search contacts'
      };
    }
  }

  /**
   * Search clients globally across name field
   * @param {string} searchTerm - The search query
   * @param {number} limit - Maximum number of results to return
   * @returns {Promise<{data: Array, totalCount: number, error: Error|null}>}
   */
  static async searchClients(searchTerm, limit = 10) {
    try {
      if (!searchTerm || searchTerm.trim().length === 0) {
        return { data: [], totalCount: 0, error: null };
      }

      const trimmedSearch = searchTerm.trim().toLowerCase();

      // Search clients by name
      const { data, error, count } = await supabase
        .from('clients')
        .select(`
          id,
          name,
          client_status,
          client_of,
          lead_source,
          services_provided,
          brand,
          trimble_formats,
          address,
          company_phone,
          state,
          reason,
          level_of_detail,
          needs_handicap_ramps
        `, { count: 'exact' })
        .ilike('name', `%${trimmedSearch}%`)
        .order('name', { ascending: true })
        .limit(limit);

      if (error) {
        console.error('Client search error:', error);
        throw error;
      }

      // Transform data to frontend format
      const transformedData = (data || []).map(client => ({
        id: client.id,
        name: client.name || '',
        clientStatus: client.client_status || '',
        clientOf: client.client_of || '',
        leadSource: client.lead_source || '',
        servicesProvided: client.services_provided || [],
        brand: client.brand || [],
        trimbleFormats: client.trimble_formats || [],
        address: client.address || '',
        companyPhone: client.company_phone || '',
        state: client.state || '',
        reason: client.reason || '',
        levelOfDetail: client.level_of_detail || '',
        needsHandicapRamps: client.needs_handicap_ramps || false,
        type: 'client' // Add type identifier for search results
      }));

      return {
        data: transformedData,
        totalCount: count || transformedData.length,
        error: null
      };
    } catch (error) {
      console.error('Client SearchService error:', error);
      return {
        data: [],
        totalCount: 0,
        error: error.message || 'Failed to search clients'
      };
    }
  }

  /**
   * Search projects, clients, and contacts
   * @param {string} searchTerm - The search query
   * @param {number} limit - Maximum number of results to return
   * @returns {Promise<{projects: Array, clients: Array, contacts: Array, totalCount: number, error: Error|null}>}
   */
  static async searchAll(searchTerm, limit = 10) {
    try {
      const projectsPromise = this.searchProjects(searchTerm, Math.ceil(limit * 0.6)); // 60% projects
      const clientsPromise = this.searchClients(searchTerm, Math.ceil(limit * 0.2)); // 20% clients
      const contactsPromise = this.searchContacts(searchTerm, Math.ceil(limit * 0.2)); // 20% contacts

      const [projectsResult, clientsResult, contactsResult] = await Promise.all([
        projectsPromise,
        clientsPromise,
        contactsPromise
      ]);

      return {
        projects: projectsResult.data || [],
        clients: clientsResult.data || [],
        contacts: contactsResult.data || [],
        totalCount: (projectsResult.totalCount || 0) + (clientsResult.totalCount || 0) + (contactsResult.totalCount || 0),
        error: projectsResult.error || clientsResult.error || contactsResult.error
      };
    } catch (error) {
      console.error('Combined search error:', error);
      return {
        projects: [],
        clients: [],
        contacts: [],
        totalCount: 0,
        error: error.message || 'Failed to search'
      };
    }
  }

  /**
   * Get a single project by ID with all details
   * @param {string} projectId - The project ID
   * @returns {Promise<{data: Object|null, error: Error|null}>}
   */
  static async getProjectDetails(projectId) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          clients (
            id,
            name
          ),
          contacts (
            id,
            name,
            email,
            phone_number,
            role
          ),
          model_builder_employee:employees!model_builder_id (
            id,
            name,
            email
          ),
          ready_for_employee:employees!ready_for_employee_id (
            id,
            name,
            email
          )
        `)
        .eq('id', projectId)
        .single();

      if (error) throw error;

      // Transform to frontend format
      const transformed = {
        id: data.id,
        projectName: data.name,
        jobNumber: data.job_number,
        rev: data.rev,
        modelingStatus: data.modeling_status,
        modelingState: data.modeling_state,
        billingStatus: data.billing_status,
        dueDate: data.due_date,
        projectedCompletionDate: data.projected_completion_date,
        completedDate: data.completed_date,
        stageStatus: data.stage_status,
        isHighPriority: data.is_high_priority,
        approximateAcreage: data.approximate_acreage,
        clientId: data.client_id,
        clientName: data.clients?.name || '',
        contactId: data.contact_id,
        contact: data.contacts || null,
        modelBuilderId: data.model_builder_id,
        modelBuilder: data.model_builder_employee || null,
        readyForEmployeeId: data.ready_for_employee_id,
        readyForEmployee: data.ready_for_employee || null,
        createdAt: data.created_at,
        lastModifiedAt: data.last_modified_at,
        lastModifiedByEmail: data.last_modified_by_email,
        // Project Information fields
        modelRequestAdditionalInfo: data.model_request_additional_info || null,
        additionalRequests: data.additional_requests || [],
        fileFormatsNeeded: data.file_formats_needed || [],
        templateFilter: data.template_filter || [],
        erosion_due_date: data.erosion_due_date || null,
        finish_grade_due_date: data.finish_grade_due_date || null,
        // Hold-related fields
        holdReason: data.hold_reason || null,
        holdNotes: data.hold_notes || null,
        holdStartDate: data.hold_start_date || null,
        lastStageChangeDate: data.last_stage_change_date || null
      };

      return { data: transformed, error: null };
    } catch (error) {
      console.error('Get project details error:', error);
      return { data: null, error: error.message };
    }
  }

  /**
   * Get related revisions for a project (same job_number, different rev)
   * @param {string} jobNumber - The job number to find revisions for
   * @param {number} excludeId - Project ID to exclude (current project)
   * @returns {Promise<{data: Array|null, error: Error|null}>}
   */
  static async getRelatedRevisions(jobNumber, excludeId = null) {
    try {
      if (!jobNumber) {
        return { data: [], error: null };
      }

      let query = supabase
        .from('projects')
        .select(`
          id,
          name,
          job_number,
          rev,
          modeling_status,
          modeling_state,
          parent_project_id
        `)
        .eq('job_number', jobNumber)
        .order('rev', { ascending: true });

      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform to frontend format
      const transformed = (data || []).map(project => ({
        id: project.id,
        projectName: project.name,
        jobNumber: project.job_number,
        rev: project.rev,
        modelingStatus: project.modeling_status,
        modelingState: project.modeling_state,
        parentProjectId: project.parent_project_id
      }));

      return { data: transformed, error: null };
    } catch (error) {
      console.error('Get related revisions error:', error);
      return { data: [], error: error.message };
    }
  }

  /**
   * Get subitems for a project with filtering options
   * @param {string} projectId - The project ID
   * @param {Object} options - Filter options
   * @returns {Promise<{data: Array|null, error: Error|null}>}
   */
  static async getProjectSubitems(projectId, options = {}) {
    try {
      let query = supabase
        .from('subitems')
        .select(`
          id,
          project_id,
          task_name,
          phase,
          status,
          priority,
          due_date,
          notes,
          modeling_status_link,
          ready_for,
          created_at,
          last_modified_at
        `)
        .eq('project_id', projectId)
        .order('phase', { ascending: true })
        .order('task_name', { ascending: true });

      // Apply status filter if provided
      if (options.status) {
        query = query.eq('status', options.status);
      }

      // Apply phase filter if provided
      if (options.phase) {
        query = query.eq('phase', options.phase);
      }

      // Limit results for performance
      const limit = options.limit || 50;
      query = query.limit(limit);

      const { data, error } = await query;

      if (error) throw error;

      // Group subitems by phase for easier display
      const groupedData = {};
      if (data) {
        data.forEach(item => {
          const phase = item.phase || 'Uncategorized';
          if (!groupedData[phase]) {
            groupedData[phase] = {
              phase,
              items: [],
              counts: {
                total: 0,
                done: 0,
                working: 0,
                stuck: 0,
                na: 0
              }
            };
          }

          groupedData[phase].items.push(item);
          groupedData[phase].counts.total++;

          // Update status counts
          const status = (item.status || '').toLowerCase();
          if (status === 'done') groupedData[phase].counts.done++;
          else if (status.includes('working')) groupedData[phase].counts.working++;
          else if (status === 'stuck') groupedData[phase].counts.stuck++;
          else if (status === 'n/a') groupedData[phase].counts.na++;
        });
      }

      return {
        data: {
          items: data || [],
          grouped: groupedData,
          totalCount: data?.length || 0
        },
        error: null
      };
    } catch (error) {
      console.error('Get project subitems error:', error);
      return { data: null, error: error.message };
    }
  }
}